import type { Category } from '../../components/agent/scheduleDay'
import { PAY_PERIOD_DATES } from '../agent/payPeriod19'
import { PAY_PERIOD_20_DATES } from '../agent/payPeriod20'
import { cellKey, parseCellKey } from './planningBoardCellKey'

/**
 * A supervisor's own unsaved changes to the planning board, held the same
 * way pendingDraftStore.ts holds an agent's: sessionStorage keyed by
 * whoever is signed in, further keyed by the team's id, since a
 * supervisor who owns more than one team (TeamSwitcher.tsx) edits each
 * team's own board independently. `key` is the board's own cellKey
 * (period, employee name, and column, not row index: a transfer can
 * reorder a team's roster, and a row-indexed key would silently point at
 * whoever a later reshuffle happened to land on that row), so one draft
 * covers every cell changed across both pay periods of one team at once.
 * There is no publish yet: this is "Save a
 * working draft" from docs/design/journey-map.md's "Planning board", not
 * "Publish the completed schedule", which is its own, later capability.
 */
export type BoardOverrideEntry = {
  key: string
  category: Category
  label: string
  detail?: string
}

const STORAGE_KEY = 'muster.supervisor.planningBoardDraft'

function storageKey(userId: string, teamId: string): string {
  return `${STORAGE_KEY}:${userId}:${teamId}`
}

export function getBoardDraft(userId: string, teamId: string): BoardOverrideEntry[] {
  try {
    const raw = sessionStorage.getItem(storageKey(userId, teamId))
    return raw ? (JSON.parse(raw) as BoardOverrideEntry[]) : []
  } catch {
    return []
  }
}

// Returns whether the write actually landed, the same as
// pendingDraftStore.ts's savePendingDraft: storage can be unavailable, and
// the caller needs to know before it confirms a save that did not happen.
export function saveBoardDraft(userId: string, teamId: string, overrides: BoardOverrideEntry[]): boolean {
  try {
    sessionStorage.setItem(storageKey(userId, teamId), JSON.stringify(overrides))
    return true
  } catch {
    return false
  }
}

// Called by the personnel pages once a transfer lands, against the team an
// employee is leaving: their unsaved edits on that team's board name a
// schedule they no longer keep once they transfer, and leaving a stale
// entry behind would silently reapply itself if this same employee is ever
// transferred back to this team later. Scoped to this one employee's own entries, not
// the whole draft, so another employee's own unsaved edits on the same
// team survive untouched.
//
// An entry in the pre-'|' row-indexed key format names no employee at all, so
// it is left alone here; SupervisorPlanningBoardPage.tsx drops it the next
// time this team's board loads (keepCurrentKeyFormat).
export function removeEmployeeFromDraft(userId: string, teamId: string, employeeId: string): boolean {
  const current = getBoardDraft(userId, teamId)
  const remaining = current.filter((entry) => {
    return entry.key.includes('|') ? parseCellKey(entry.key).employeeId !== employeeId : true
  })
  if (remaining.length === current.length) return true
  try {
    sessionStorage.setItem(storageKey(userId, teamId), JSON.stringify(remaining))
    return true
  } catch {
    return false
  }
}

// Called by SupervisorDailyAssignmentsPage.tsx when the supervisor saves a
// day: the planning board owns the schedule entry a reassignment changes, so a
// day saved on the daily board becomes the same day's cell in this team's
// planning board draft, ready to publish. Only the current pay period has a
// daily board, so the cells are its own.
export function applyDailyAssignments(
  userId: string,
  teamId: string,
  dayIndex: number,
  saved: { employeeId: string; category: Category; label: string; detail?: string }[],
): boolean {
  const touched = new Set(saved.map((entry) => cellKey('current', entry.employeeId, dayIndex)))
  const next = getBoardDraft(userId, teamId).filter((entry) => !touched.has(entry.key))
  for (const entry of saved) {
    next.push({ key: cellKey('current', entry.employeeId, dayIndex), category: entry.category, label: entry.label, detail: entry.detail })
  }
  return saveBoardDraft(userId, teamId, next)
}

// What this team's planning board draft holds for one day of the current pay
// period, by employee: read by the daily assignment board, which shows the day
// as the planning board has it rather than as it was last published.
export function overridesForDay(
  userId: string,
  teamId: string,
  dayIndex: number,
): Map<string, { category: Category; label: string; detail?: string }> {
  const byEmployee = new Map<string, { category: Category; label: string; detail?: string }>()
  for (const entry of getBoardDraft(userId, teamId)) {
    if (!entry.key.includes('|')) continue
    const { period, employeeId, col } = parseCellKey(entry.key)
    if (period === 'current' && col === dayIndex) byEmployee.set(employeeId, { category: entry.category, label: entry.label, detail: entry.detail })
  }
  return byEmployee
}

// Called by SupervisorApprovalInboxPage.tsx once a leave request is
// approved (or approved with modification): entryFor's own precedence
// (SupervisorPlanningBoardPage.tsx) lets a board override replace an
// approved leave cell once one exists, so an override already sitting on
// a cell the leave now covers, saved before this approval and blind to
// it, would otherwise go on masking the leave it should be showing
// instead. Scoped to exactly the cells this approval covers, both pay
// periods at once, since either one's own dates can be affected. A
// supervisor who wants to override the leave can still do so with a
// fresh edit made after this point; that edit is unaffected here.
export function clearOverridesForApprovedLeave(
  userId: string,
  teamId: string,
  employeeId: string,
  approvedDates: string[],
): boolean {
  const dateSet = new Set(approvedDates)
  const keysToClear = new Set<string>()
  PAY_PERIOD_DATES.forEach((day, col) => {
    if (dateSet.has(day.date)) keysToClear.add(cellKey('current', employeeId, col))
  })
  PAY_PERIOD_20_DATES.forEach((day, col) => {
    if (dateSet.has(day.date)) keysToClear.add(cellKey('next', employeeId, col))
  })
  if (keysToClear.size === 0) return true

  const current = getBoardDraft(userId, teamId)
  const remaining = current.filter((entry) => {
    return entry.key.includes('|') ? !keysToClear.has(entry.key) : true
  })
  if (remaining.length === current.length) return true
  try {
    sessionStorage.setItem(storageKey(userId, teamId), JSON.stringify(remaining))
    return true
  } catch {
    return false
  }
}

// Read-time counterpart to clearOverridesForApprovedLeave: that one only
// ever runs against a leave decide() itself just approved, so a decision
// already on record before entryFor's own leave-vs-override precedence
// existed (SupervisorPlanningBoardPage.tsx) keeps whatever override it
// predates invisible under. Called against the team's whole draft and the full
// current approvedLeaveByCell map, not one decision's own dates, so it
// catches every approval already on record in one pass; SupervisorPlanning
// BoardPage.tsx calls it before loadTeamData reads the same draft, the
// same ordering clearOverridesForApprovedLeave's own callers already need
// for the same reason.
export function migrateOverridesForApprovedLeave(
  userId: string,
  teamId: string,
  approvedLeaveByCell: Map<string, { label: string }>,
): boolean {
  if (approvedLeaveByCell.size === 0) return true
  const current = getBoardDraft(userId, teamId)
  const remaining = current.filter((entry) => {
    if (!entry.key.includes('|')) return true
    const { period, employeeId, col } = parseCellKey(entry.key)
    const date = (period === 'current' ? PAY_PERIOD_DATES : PAY_PERIOD_20_DATES)[col]
    return date ? !approvedLeaveByCell.has(`${employeeId}|${date.date}`) : true
  })
  if (remaining.length === current.length) return true
  try {
    sessionStorage.setItem(storageKey(userId, teamId), JSON.stringify(remaining))
    return true
  } catch {
    return false
  }
}
